import { Button, ButtonGroup, Checkbox, Tooltip, useBreakpoint } from 'slab'
import { formatMoney } from 'utils'

import { type MedicalPlan, type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { type OnboardingEnrollment, type PlanCardBadgeCategory } from '../types'
import {
  calculateRemainingBudgetForTieredAllowance,
  extractInNetworkAmount,
  extractInNetworkString,
  getDeductibleTooltipLabel,
  getRecommendationReasonLabels,
  MOOP_INFO,
  PRIMARY_DOCTOR_VISITS_INFO,
  SPECIALIST_DOCTOR_VISITS_INFO,
} from '../utils'
import { MedicalPlanBadges } from './MedicalPlanBadges'
import {
  getRemainingAllowanceWithEnrollments,
  SelectPlanButton,
} from './MemberOnboardingPlanCard'
import { PlanCard } from './PlanCard'
import { PlanChip } from './PlanChip'
import { ProviderInNetworkMap } from './ProviderInNetworkMap'
import { ProviderInNetworkVerification } from './ProviderInNetworkVerification'
import { MAX_PLANS_TO_VERIFY, useProviderInNetworkVerificationContext } from './ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'

export type MedicalPlanProps = {
  badgeCategories?: PlanCardBadgeCategory[];
  currentPlanEnrollment?: OnboardingEnrollment | null;
  planDisplayItem: MedicalPlanDisplayItem;
  onSelectPlan?: (plan: MedicalPlan) => void;
  setShowMedicalPlanDetail: (plan: MedicalPlanDisplayItem) => void;
  selectedPlansForComparison?: MedicalPlanDisplayItem[];
  onPlanComparisonToggle?: (
    plan: MedicalPlanDisplayItem,
    isSelected: boolean
  ) => void;
  onTogglePlanVerification?: (planId: string, isSelected: boolean) => void;
  onSelectMorePlansForVerification?: () => void;
  showInNetworkVerificationCta?: boolean;
}

export function MedicalPlanCard ({
  badgeCategories,
  currentPlanEnrollment,
  onSelectPlan,
  planDisplayItem,
  setShowMedicalPlanDetail,
  selectedPlansForComparison,
  onPlanComparisonToggle,
  onTogglePlanVerification,
  onSelectMorePlansForVerification,
  showInNetworkVerificationCta,
}: MedicalPlanProps) {
  const { onboardingData, isFerryVerificationTasksEnabled } = useOnboarding()
  const { canAddMorePlans, selectedPlanIds } = useProviderInNetworkVerificationContext()
  const { breakpointIsAtLeast } = useBreakpoint()

  const plan = planDisplayItem.plan

  const premium = Number(
    ((planDisplayItem.total_premium.subunits || 0) / 100).toFixed(2)
  )

  const isMemberOnTieredAllowance = !!planDisplayItem.tiered_allowance_employee_only_premium
  let difference = null
  if (isMemberOnTieredAllowance) {
    difference = calculateRemainingBudgetForTieredAllowance({
      planDisplayItem,
    })
  } else if (onboardingData) {
    difference = getRemainingAllowanceWithEnrollments(
      onboardingData,
      premium,
      'medical'
    )
  }

  const isIndividual =
    (onboardingData?.additional_participants || []).length === 0

  const isSelected =
    currentPlanEnrollment &&
    currentPlanEnrollment.carrier_name?.toLowerCase() ===
      plan.carrier_name.toLowerCase() &&
    currentPlanEnrollment.plan_code?.toLowerCase() === plan.id

  const isComparisonSelected = selectedPlansForComparison?.some(
    (p) => p.plan.id === plan.id
  )
  const isVerificationSelected = selectedPlanIds.includes(plan.id)

  const parsedDeductible = extractInNetworkAmount(
    isIndividual
      ? plan.individual_medical_deductible
      : plan.family_medical_deductible
  )
  const deductibleValue =
    typeof parsedDeductible === 'string'
      ? parsedDeductible
      : formatMoney(parsedDeductible)

  const parsedMOOP = extractInNetworkAmount(
    isIndividual ? plan.individual_medical_moop : plan.family_medical_moop
  )

  const providerInNetworkMap = planDisplayItem.provider_in_network_map
  const inNetworkProvidersCount = planDisplayItem.in_network_providers_count

  const labels: { cardLabel: string; tooltipLabel: string, tableLabel: string }[] = []
  if (badgeCategories && badgeCategories.length > 0) {
    badgeCategories.forEach((category) => {
      const label = getRecommendationReasonLabels(category)
      labels.push(label)
    })
  }

  const premiumComponent = difference !== null && (
    <PlanCard.Premium
      align={breakpointIsAtLeast('xl') ? 'right' : 'left'}
      amount={premium}
      remainder={difference}
      isTieredAllowance={isMemberOnTieredAllowance}
    />
  )
  const hasInNetworkProviders = providerInNetworkMap && inNetworkProvidersCount !== undefined

  const planDisabledVerificationCheckbox = !canAddMorePlans && !selectedPlanIds.includes(plan.id)

  return (
    <>
      {badgeCategories && badgeCategories.length > 0 && (
        <div className='flex'>
          {labels.map((label) => (
            <div className='text-left' key={label.cardLabel}>
              <Tooltip content={label.tooltipLabel}>
                <div className='ml-2 px-4 py-2 rounded-t-lg inline-block bg-violet-50 text-violet-500 body-sm-bold'>
                  {label.cardLabel}
                </div>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
      <PlanCard
        layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
        selected={
          !!isSelected ||
          (badgeCategories?.includes('renewal') && !currentPlanEnrollment) ||
          (onTogglePlanVerification && isVerificationSelected)
        }
        footer={
          onTogglePlanVerification && (
            <Tooltip enabled={planDisabledVerificationCheckbox} content={`A maximum of ${MAX_PLANS_TO_VERIFY} plans can be selected`}>
              <div className='w-fit'>
                <Checkbox
                  checked={isVerificationSelected}
                  onChange={(e) => onTogglePlanVerification(plan.id, e.target.checked)}
                  label='Add this plan to the call list'
                  disabled={planDisabledVerificationCheckbox}
                />
              </div>
            </Tooltip>
          )
        }
      >
        <PlanCard.GraySection wideMode>
          <div className='flex flex-col gap-4 h-full'>
            <PlanChip
              carrierLogo={plan.carrier_image_url ?? ''}
              carrierName={plan.carrier_name}
              planName={plan.friendly_name}
            />
            {/* Badges */}
            <MedicalPlanBadges
              bordered
              planHsaEligible={plan.hsa_eligible}
              planMetalTier={plan.metal_tier}
              planType={plan.plan_type}
            />
            {hasInNetworkProviders && !isFerryVerificationTasksEnabled && (
              <ProviderInNetworkMap
                providerInNetworkMap={providerInNetworkMap}
                inNetworkProvidersCount={inNetworkProvidersCount}
                providerSupportLink={plan.provider_support_link}
              />
            )}
            {hasInNetworkProviders && isFerryVerificationTasksEnabled && (
              <>
                <ProviderInNetworkVerification
                  providerInNetworkMap={providerInNetworkMap}
                  providerSupportLink={plan.provider_support_link}
                  onSelectMorePlansForVerification={onSelectMorePlansForVerification}
                  showInNetworkVerificationCta={showInNetworkVerificationCta}
                  planId={plan.id}
                />
              </>
            )}
            {
              onPlanComparisonToggle && selectedPlansForComparison && (
                <div className='mt-auto'>
                  <Checkbox
                    label='Compare plan'
                    checked={isComparisonSelected}
                    onChange={(e) =>
                      onPlanComparisonToggle(planDisplayItem, e.target.checked)}
                    disabled={
                      !isComparisonSelected &&
                      selectedPlansForComparison.length >= 3
                    }
                  />
                </div>
              )
            }
          </div>
        </PlanCard.GraySection>
        <PlanCard.Section>
          <div className='flex flex-col gap-6 h-full'>
            <div className='flex flex-col xl:flex-row gap-6 h-full justify-between'>
              {!breakpointIsAtLeast('xl') && premiumComponent}
              <PlanCard.Features>
                <PlanCard.Feature
                  detail={deductibleValue}
                  term={isIndividual ? 'Deductible' : 'Family deductible'}
                  info={getDeductibleTooltipLabel(
                    parsedDeductible,
                    isIndividual
                  )}
                />
                <PlanCard.Feature
                  detail={
                    typeof parsedMOOP === 'string'
                      ? parsedMOOP
                      : formatMoney(parsedMOOP)
                  }
                  term={
                    isIndividual
                      ? 'Out-of-pocket max'
                      : 'Family out-of-pocket max'
                  }
                  info={MOOP_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(
                    plan.primary_care_physician_benefit
                  )}
                  term='Primary doctor visits'
                  info={PRIMARY_DOCTOR_VISITS_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(plan.specialist_benefit)}
                  term='Specialist doctor visits'
                  info={SPECIALIST_DOCTOR_VISITS_INFO}
                />
              </PlanCard.Features>
              {breakpointIsAtLeast('xl') && premiumComponent}
            </div>
            <ButtonGroup align='right' size='small'>
              <Button
                variant='tertiary-deemphasized'
                onClick={() => setShowMedicalPlanDetail(planDisplayItem)}
              >
                View details
              </Button>
              {
                onSelectPlan && (
                  <SelectPlanButton
                    isCurrentPlan={currentPlanEnrollment}
                    isSelected={isSelected}
                    handleSelectPlan={() => onSelectPlan(plan)}
                  />
                )
              }
            </ButtonGroup>
          </div>
        </PlanCard.Section>
      </PlanCard>
    </>
  )
}
